import { useMemo } from 'react';
import { Chart, getCommonTimelineChartOptionsStatic } from 'src/common/components/Chart';

import SkeletonChartCard from '@/components/SkeletonChart';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Text,
  ToggleGroup,
  ToggleGroupItem,
} from '@/design-system/components/ui';
import { CHART_COLORS } from '@/design-system/tokens';

import { type BreakdownGranularity, type StatsBreakdownEntry } from '../domain/types';

const SERIES_DEFS: {
  name: string;
  metric: 'direct_volume' | 'indirect_volume' | 'direct_revenue' | 'indirect_revenue';
  color: string;
}[] = [
  { name: 'Direct Volume', metric: 'direct_volume', color: CHART_COLORS.primary.dark },
  { name: 'Indirect Volume', metric: 'indirect_volume', color: CHART_COLORS.secondary.dark },
  { name: 'Direct Revenue', metric: 'direct_revenue', color: CHART_COLORS.success.dark },
  { name: 'Indirect Revenue', metric: 'indirect_revenue', color: CHART_COLORS.info.dark },
];

const GRANULARITY_OPTIONS: { value: BreakdownGranularity; label: string }[] = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'all', label: 'All' },
];

export interface ReferralBreakdownChartProps {
  breakdown: StatsBreakdownEntry[];
  isLoading: boolean;
  granularity?: BreakdownGranularity;
  onGranularityChange?: (granularity: BreakdownGranularity) => void;
}

export function ReferralBreakdownChart({
  breakdown,
  isLoading,
  granularity,
  onGranularityChange,
}: ReferralBreakdownChartProps) {
  const chartOptions = useMemo(() => {
    const base = getCommonTimelineChartOptionsStatic();
    return {
      ...base,
      colors: SERIES_DEFS.map((s) => s.color),
      stroke: { ...(base.stroke ?? {}), curve: 'smooth' as const, width: 3 },
      legend: {
        ...base.legend,
        position: 'bottom' as const,
        labels: { colors: 'var(--muted-foreground)' },
      },
      yaxis: {
        ...base.yaxis,
        labels: {
          formatter: (value: number) => {
            if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
            if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
            return `$${value.toFixed(0)}`;
          },
          style: { colors: 'var(--muted-foreground)' },
        },
      },
      xaxis: {
        ...base.xaxis,
        labels: {
          ...base.xaxis?.labels,
          style: { colors: 'var(--muted-foreground)' },
        },
      },
      tooltip: {
        ...base.tooltip,
        y: {
          formatter: (value: number) =>
            value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }),
        },
      },
    };
  }, []);

  const series = useMemo(() => {
    if (breakdown.length === 0) return [];
    const sorted = [...breakdown].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return SERIES_DEFS.map(({ name, metric }) => ({
      name,
      data: sorted.map((entry) => ({ x: new Date(entry.date).getTime(), y: entry[metric] ?? 0 })),
    }));
  }, [breakdown]);

  const granularityToggle = granularity && onGranularityChange && (
    <ToggleGroup
      type="single"
      value={granularity}
      onValueChange={(value) => {
        if (value) onGranularityChange(value as BreakdownGranularity);
      }}
      size="sm"
    >
      {GRANULARITY_OPTIONS.map((opt) => (
        <ToggleGroupItem key={opt.value} value={opt.value}>
          {opt.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );

  if (isLoading) {
    return <SkeletonChartCard chartTitle="Referral Volume & Revenue" />;
  }

  if (breakdown.length === 0) {
    return (
      <Card>
        <CardHeader className="tw:flex-row tw:items-center tw:justify-between">
          <CardTitle>Referral Volume & Revenue</CardTitle>
          {granularityToggle}
        </CardHeader>
        <CardContent>
          <div className="tw:flex tw:items-center tw:justify-center tw:py-16">
            <Text variant="body-md" color="muted">
              No data for this period
            </Text>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="tw:flex-row tw:items-center tw:justify-between">
        <CardTitle>Referral Volume & Revenue</CardTitle>
        {granularityToggle}
      </CardHeader>
      <CardContent>
        <Chart height={350} options={chartOptions} series={series} type="line" width="100%" />
      </CardContent>
    </Card>
  );
}
